import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Modal } from '../../../components/Modal';
import { dmsService } from '../../../services/dmsService';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: any | null;
  onEditClick: () => void;
  onRestore: () => void;
  onRefresh?: () => void;
  isArchivedView?: boolean;
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  document,
  onEditClick,
  onRestore,
  onRefresh,
  isArchivedView = false,
}) => {
  const [history, setHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shareVersion, setShareVersion] = useState<any | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canDownload = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  useEffect(() => {
    const fetchHistory = async () => {
      if (!document) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await dmsService.getDocumentHistory(document.id);
        setHistory(data);
      } catch (err: any) {
        console.error('Failed to fetch document history:', err);
        setError(err.message || 'Failed to load history.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && document) {
      fetchHistory();
    }
  }, [isOpen, document]);

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadVersion = async (versionNumber: number) => {
    try {
      if (!document) return;
      const res = await dmsService.downloadDocument(document.id, versionNumber);
      if (res.download_url) {
        window.location.href = res.download_url;
      }
    } catch (e) {
      console.error('Download failed', e);
      alert('Failed to generate secure download link for this version.');
    }
  };

  const handleArchiveVersion = async (versionId: string) => {
    if (!document) return;
    if (!window.confirm('Are you sure you want to archive this version?')) return;
    setIsLoading(true);
    try {
      await dmsService.archiveDocumentVersion(document.id, versionId);
      const data = await dmsService.getDocumentHistory(document.id);
      
      const hasActiveVersions = data.some((v: any) => !v.is_archived);
      if (!hasActiveVersions) {
        alert('All versions of this document have been archived. The document is now moved to Archived Documents.');
        onRestore(); // Closes modal and refreshes dashboard list
      } else {
        setHistory(data);
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (err: any) {
      console.error('Failed to archive document version:', err);
      alert(err.message || 'Failed to archive document version.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleRestoreDocument = async () => {
    if (!document) return;
    if (!window.confirm('Are you sure you want to restore this document to the active list?')) return;
    setIsLoading(true);
    try {
      await dmsService.updateDocument(document.id, { is_archived: false });
      alert('Document restored successfully.');
      onRestore();
    } catch (err: any) {
      console.error('Failed to restore document:', err);
      alert(err.message || 'Failed to restore document.');
      setIsLoading(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    if (!document) return;
    if (!window.confirm('Are you sure you want to restore this version?')) return;
    setIsLoading(true);
    try {
      await dmsService.restoreDocumentVersion(document.id, versionId);
      alert('Version restored successfully.');
      onRestore();
    } catch (err: any) {
      console.error('Failed to restore document version:', err);
      alert(err.message || 'Failed to restore document version.');
      setIsLoading(false);
    }
  };


  const openShareModal = (version: any) => {
    setShareVersion(version);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const closeShareModal = () => {
    setShareVersion(null);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) {
      setShareError('Please enter a recipient email.');
      return;
    }
    setShareLoading(true);
    setShareError(null);
    try {
      await dmsService.shareDocument(document.id, shareEmail.trim(), shareVersion.version_number);
      setShareSuccess(true);
    } catch (err: any) {
      setShareError(err.message || 'Failed to send email.');
    } finally {
      setShareLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Version History: ${document?.title || ''}`}>
        <div className="modal-body">
          {error && (
            <div
              style={{
                color: '#dc2626',
                backgroundColor: '#fee2e2',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
              Loading history...
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
              No history records found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.75rem 1rem' }}>Ver</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Filename</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Size</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Uploaded By</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Upload Date</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                    <th style={{ padding: '0.75rem 1rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((version) => {
                    const isCurrent = document?.current_version_detail?.id === version.id;
                    return (
                      <tr
                        key={version.id}
                        style={{
                          backgroundColor: version.is_archived
                            ? 'rgba(239, 68, 68, 0.01)'
                            : isCurrent
                              ? 'rgba(71, 49, 146, 0.02)'
                              : 'transparent',
                          opacity: version.is_archived ? 0.75 : 1,
                        }}
                      >
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <strong>v{version.version_number}</strong>
                        </td>
                        <td
                          style={{ padding: '0.75rem 1rem', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={version.original_name}
                        >
                          {version.original_name}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>{formatSize(version.file_size)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {version.uploaded_by_detail?.full_name || version.uploaded_by_detail?.user_name}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {new Date(version.created_at).toLocaleString()}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {document?.is_archived ? (
                            isCurrent ? (
                              <span className="status-badge" style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db' }}>
                                Archived (Latest)
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                                Superseded
                              </span>
                            )
                          ) : version.is_archived ? (
                            <span className="status-badge" style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db' }}>
                              Archived
                            </span>
                          ) : isCurrent ? (
                            <span className="status-badge status-active" style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem' }}>
                              Active
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                              Superseded
                            </span>
                          )}
                        </td>

                        <td style={{ padding: '0.75rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {isArchivedView ? (

                            (document?.is_archived || version.is_archived) && (

                              <button
                                className="secondary-btn sm-btn"
                                onClick={
                                  document?.is_archived
                                    ? handleRestoreDocument
                                    : () => handleRestoreVersion(version.id)
                                }
                                style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                                title={document?.is_archived ? "Restore Document" : "Restore Version"}
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H17" />
                                </svg>
                                Restore
                              </button>
                            )
                          ) : (

                            <>
                              {canDownload && (
                                <button
                                  className="secondary-btn sm-btn"
                                  onClick={() => handleDownloadVersion(version.version_number)}
                                  title="Download"
                                >
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </button>
                              )}
                              <button
                                className="secondary-btn sm-btn"
                                onClick={() => onEditClick()}
                                title="Edit Document Metadata"
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                className="secondary-btn sm-btn"
                                onClick={() => openShareModal(version)}
                                title="Share via email"
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Share
                              </button>
                              {document?.can_archive_versions && !version.is_archived && (
                                <button
                                  className="secondary-btn sm-btn"
                                  onClick={() => handleArchiveVersion(version.id)}
                                  style={{ borderColor: '#fca5a5', color: '#ef4444' }}
                                  title="Archive Version"
                                >
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                  </svg>
                                  Archive
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </Modal>

      {shareVersion && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeShareModal(); }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '380px',
              boxShadow: '0 8px 32px rgba(71, 49, 146, 0.18)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                Share File
              </h3>
              <button
                onClick={closeShareModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px' }}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
              Sharing <strong style={{ color: '#111827' }}>{document?.title}</strong> — v{shareVersion.version_number}
            </p>

            {shareSuccess ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: '#f5f3ff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 0.75rem',
                }}>
                  <svg fill="none" stroke="#473192" viewBox="0 0 24 24" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#374151', fontWeight: 600 }}>
                  Email sent successfully!
                </p>
                <button className="secondary-btn" onClick={closeShareModal}>Done</button>
              </div>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                  Recipient Email
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
                  style={{
                    width: '100%',
                    padding: '0.55rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    marginBottom: shareError ? '0.5rem' : '1rem',
                  }}
                  autoFocus
                />
                {shareError && (
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#dc2626' }}>
                    {shareError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="secondary-btn" onClick={closeShareModal} disabled={shareLoading}>
                    Cancel
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={shareLoading}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: shareLoading ? '#9ca3af' : '#473192',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: shareLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {shareLoading ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
